import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View
} from 'react-native';
import type { SortableGridRenderItem } from 'react-native-sortables';
import Sortable from 'react-native-sortables';

import { MediaItemPlaceholderImage } from '@/features/media/components/MediaItemPlaceholderImage';
import type { MediaItemPlaceholder } from '@/features/media/types/picker';

const ITEM_GAP = 16;
const UPLOADING_KEY = '__uploading__';
const UPLOADING_PLACEHOLDER: MediaItemPlaceholder = {
  temporaryMediaItemId: UPLOADING_KEY
};

const HORIZONTAL_CONTAINER_STYLE: ViewStyle = { width: 100, height: 80 };

const getItemKey = (item: MediaItemPlaceholder) =>
  item.mediaItemId ?? item.temporaryMediaItemId ?? '';

const isUploadingPlaceholder = (item: MediaItemPlaceholder) =>
  item.temporaryMediaItemId === UPLOADING_KEY;

const styles = StyleSheet.create({
  horizontal: { gap: ITEM_GAP, flexDirection: 'row', alignItems: 'flex-start' },
  grid: {
    gap: ITEM_GAP,
    padding: 12,
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignItems: 'center'
  }
});

type Props = {
  layout?: 'horizontal' | 'grid';
  columns?: number;
  style?: StyleProp<ViewStyle>;
  /**
   * Only available in case of horizontal layout
   */
  contentContainerStyle?: StyleProp<ViewStyle>;
  items: MediaItemPlaceholder[];
  onDelete?: (item: MediaItemPlaceholder) => void;
  /**
   * If passed the images can be reordered
   */
  onReorder?: (reordered: MediaItemPlaceholder[]) => void;
  uploading?: boolean;
};

export const MediaItemPlaceholderList: FC<Props> = ({
  layout = 'horizontal',
  columns = 3,
  style,
  contentContainerStyle,
  items,
  onDelete,
  onReorder,
  uploading = false
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const [viewWidth, setViewWidth] = useState(screenWidth);

  const isGrid = layout === 'grid';
  const sortable = Boolean(onReorder) && items.length > 1;

  const handleGridDragEnd = useCallback(
    ({ data }: { data: MediaItemPlaceholder[] }) => {
      onReorder?.(data.filter((item) => !isUploadingPlaceholder(item)));
    },
    [onReorder]
  );

  const gridItemWidth = useMemo(
    () => Math.floor((viewWidth - 24 - ITEM_GAP * (columns - 1)) / columns),
    [columns, viewWidth]
  );

  const gridContainerStyle: ViewStyle = useMemo(
    () => ({ width: gridItemWidth, height: 89, marginRight: 0, marginTop: 0 }),
    [gridItemWidth]
  );

  const gridData = useMemo(
    () => (uploading ? [...items, UPLOADING_PLACEHOLDER] : items),
    [items, uploading]
  );

  const renderGridItem = useCallback(
    (item: MediaItemPlaceholder) =>
      isUploadingPlaceholder(item) ? (
        <MediaItemPlaceholderImage
          key={UPLOADING_KEY}
          mediaItemPlaceholder={{}}
          style={gridContainerStyle}
          uploading
        />
      ) : (
        <MediaItemPlaceholderImage
          key={getItemKey(item)}
          mediaItemPlaceholder={item}
          onDelete={onDelete}
          style={gridContainerStyle}
        />
      ),
    [gridContainerStyle, onDelete]
  );

  const gridItems = useMemo(
    () => gridData.map((item) => renderGridItem(item)),
    [gridData, renderGridItem]
  );

  const horizontalPlaceholder = uploading && (
    <MediaItemPlaceholderImage
      key={UPLOADING_KEY}
      mediaItemPlaceholder={{}}
      style={HORIZONTAL_CONTAINER_STYLE}
      uploading
    />
  );

  const handleHorizontalDragEnd = useCallback(
    ({ order }: { order: <I>(data: I[]) => I[] }) => onReorder?.(order(items)),
    [items, onReorder]
  );

  const horizontalItems = useMemo(
    () =>
      [...items]
        .reverse()
        .map((item) => (
          <MediaItemPlaceholderImage
            key={getItemKey(item)}
            mediaItemPlaceholder={item}
            onDelete={onDelete}
            style={HORIZONTAL_CONTAINER_STYLE}
          />
        )),
    [items, onDelete]
  );

  if (isGrid && sortable) {
    const renderSortableGridItem: SortableGridRenderItem<
      MediaItemPlaceholder
    > = ({ item }) => {
      const isPlaceholder = isUploadingPlaceholder(item);
      return (
        <Sortable.Handle mode={isPlaceholder ? 'fixed-order' : 'draggable'}>
          {renderGridItem(item)}
        </Sortable.Handle>
      );
    };

    return (
      <View
        style={[{ padding: 12 }, style]}
        onLayout={(e) => setViewWidth(Math.max(1, e.nativeEvent.layout.width))}
      >
        <Sortable.Grid
          data={gridData}
          renderItem={renderSortableGridItem}
          keyExtractor={getItemKey}
          columns={columns}
          rowGap={ITEM_GAP}
          columnGap={ITEM_GAP}
          customHandle
          onDragEnd={handleGridDragEnd}
          inactiveItemOpacity={1}
        />
      </View>
    );
  }

  if (isGrid) {
    return (
      <View
        style={[styles.grid, style]}
        onLayout={(e) => setViewWidth(Math.max(1, e.nativeEvent.layout.width))}
      >
        {gridItems}
      </View>
    );
  }

  if (sortable) {
    return (
      <ScrollView
        horizontal
        style={style}
        contentContainerStyle={[styles.horizontal, contentContainerStyle]}
      >
        {horizontalPlaceholder}
        <Sortable.Flex
          onDragEnd={handleHorizontalDragEnd}
          gap={ITEM_GAP}
          flexWrap="nowrap"
          inactiveItemOpacity={1}
        >
          {horizontalItems}
        </Sortable.Flex>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      horizontal
      style={style}
      contentContainerStyle={[styles.horizontal, contentContainerStyle]}
    >
      {horizontalPlaceholder}
      {horizontalItems}
    </ScrollView>
  );
};
